import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { owlClient } from '../../apiUtils/client';
import { QueryKey } from '../../types';
import { useLineType } from './useLineType';
import { useAllLinesMode } from './useAllLinesMode';
import { useMergedCarrierList } from './useMergedCarrierList';
import { LinedCarrier } from './carrierLines';

export function useCarrierList() {
  const params = useParams();
  const carrierSlug = params?.carrierSlug;

  const { lineType } = useLineType();
  const { allLines, allLinesLoading } = useAllLinesMode();
  const merged = useMergedCarrierList(allLines);

  const { data: singleLineList, isLoading: singleLineLoading } = useQuery(
    [QueryKey.CarrierList, lineType],
    () =>
      owlClient.carrier.fetchList({
        query: { insuranceLine: lineType },
      }),
    {
      enabled: !allLines && !allLinesLoading,
      select: (data) => data.data,
    },
  );

  const carrierList: LinedCarrier[] | undefined = allLines
    ? merged.carrierList
    : singleLineList;
  const carrierListLoading =
    allLinesLoading ||
    (allLines ? merged.carrierListLoading : singleLineLoading);

  const childCarriers = useMemo(() => {
    if (carrierSlug) {
      return carrierList?.filter((c) => c.parentCarrierSlug === carrierSlug);
    }
    return null;
  }, [carrierSlug, carrierList]);

  return {
    carrierList,
    carrierListLoading,
    childCarriers,
  };
}
